import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";

// Videos are hosted on YouTube by default; set source to "cloudinary" and
// use the Cloudinary public_id as the id for videos uploaded there.
const CLOUDINARY_VIDEO_BASE = "https://res.cloudinary.com/do4kzutr7/video/upload";

interface Video {
  id: string;
  source?: "youtube" | "cloudinary";
  // Cloudinary only: extra delivery transformation for the .mp4 (e.g. to force
  // a fresh H.264 copy), and the original upload's extension (e.g. "mov"),
  // offered as a fallback source if the .mp4 can't be played.
  transformation?: string;
  originalExt?: string;
  title: string;
  description: string;
  duration: string;
  category: string;
}

const videos: Video[] = [
  {
    id: "WhatsApp_Video_2026-09-27_at_08.52.07_bjvjkp",
    source: "cloudinary",
    title: "Introduction to Fleetrify",
    description: "A quick overview of what Fleetrify does and how it helps you run your fleet, staff, orders and payments in one place.",
    //  duration: "1:03",
    duration: "1:02",
    category: "Introduction",
  },
  {
    id: "IMG_3778_20MB_h1gs5f",
    source: "cloudinary",
    title: "Getting Started: Account Creation & Onboarding",
    description: "A full walkthrough of creating your Fleetrify account and completing onboarding, from sign-up to your dashboard.",
    duration: "8:13",
    category: "Onboarding",
  },
  {
    id: "WhatsApp_Video_2026-09-23_at_22.59.16_yaiq1d",
    source: "cloudinary",
    title: "Managing Your Fleet Vehicles",
    description: "A complete walkthrough of adding, editing and tracking your vehicles.",
    duration: "1:18",
    category: "Fleet",
  },
  {
    id: "WhatsApp_Video_2026-09-25_at_00.57.16_hevokb",
    source: "cloudinary",
    title: "Staff & Roles Management",
    description: "How to invite staff, assign roles and manage permissions across your organization.",
    duration: "3:28",
    category: "Staff",
  },
  {
    id: "WhatsApp_Video_2026-09-23_at_21.54.58_svql4f",
    source: "cloudinary",
    title: "How to Create a Wallet",
    description: "Step-by-step guide to setting up your Fleetrify wallet, from verifying your BVN to setting your PIN.",
    duration: "1:02",
    category: "Finance",
  },
  // Hidden until the real analytics video is recorded — still a placeholder YouTube ID.
  // {
  //   id: "JGwWNGJdvx8",
  //   title: "Understanding Analytics & Reports",
  //   description: "Deep dive into reading your fleet analytics and exporting reports.",
  //   duration: "10:05",
  //   category: "Analytics",
  // },
  {
    id: "IMG_3856_odwp9h",
    source: "cloudinary",
    // The default .mp4 of this upload is cached without byte-range support,
    // which Safari refuses to play — serve a separately generated copy.
    transformation: "vc_h264,q_auto",
    originalExt: "mov",
    title: "Service Requests & Tracking",
    description: "How to create service requests, assign drivers and monitor progress in real time.",
    duration: "6:16",
    category: "Services",
  },
  {
    id: "WhatsApp_Video_2026-09-24_at_11.52.31_sqgr1y",
    source: "cloudinary",
    title: "Order Management",
    description: "How to view, manage and track your orders from start to finish in Fleetrify.",
    duration: "3:13",
    category: "Orders",
  },
  {
    id: "WhatsApp_Video_2026-09-24_at_00.06.05_wsln6v",
    source: "cloudinary",
    title: "Using the Messages Module",
    description: "How to chat with your staff and customers, and keep track of your conversations in Fleetrify.",
    duration: "1:04",
    category: "Messages",
  },
  {
    id: "IMG_3849_ifiivm",
    source: "cloudinary",
    originalExt: "mov",
    title: "Navigating Your Settings",
    description: "A tour of the Settings area — where to find and update your company profile, roles and permissions, and account preferences.",
    duration: "4:49",
    category: "Settings",
  },
  {
    id: "WhatsApp_Video_2026-09-23_at_21.44.34_tvho8r",
    source: "cloudinary",
    title: "How to Contact Us",
    description: "How to reach the Fleetrify support team whenever you need help.",
    duration: "0:46",
    category: "Support",
  },
];

const mp4Url = (video: Video) =>
  `${CLOUDINARY_VIDEO_BASE}/${video.transformation ? `${video.transformation}/` : ""}${video.id}.mp4`;

const thumbnailUrl = (video: Video) =>
  video.source === "cloudinary"
    ? `${CLOUDINARY_VIDEO_BASE}/so_1/${video.id}.jpg`
    : `https://img.youtube.com/vi/${video.id}/mqdefault.jpg`;

const categoryColors: Record<string, string> = {
  Introduction: "bg-sky-100 text-sky-700",
  Onboarding: "bg-blue-100 text-blue-700",
  Fleet: "bg-emerald-100 text-emerald-700",
  Staff: "bg-violet-100 text-violet-700",
  Finance: "bg-amber-100 text-amber-700",
  Analytics: "bg-cyan-100 text-cyan-700",
  Services: "bg-rose-100 text-rose-700",
  Messages: "bg-indigo-100 text-indigo-700",
  Orders: "bg-teal-100 text-teal-700",
  Settings: "bg-slate-100 text-slate-700",
  Support: "bg-orange-100 text-orange-700",
};

const VideoGuide = () => {
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState<Video>(videos[0]);
  // Browsers only allow sound when playback follows a user click, and YouTube
  // falls back to *muted* autoplay otherwise — so never autoplay on page load,
  // only once the user has picked a video from the list.
  const [userSelected, setUserSelected] = useState(false);

  const selectVideo = (video: Video) => {
    setActiveVideo(video);
    setUserSelected(true);
  };

  return (
    <DashboardLayout
      title="Video Guide"
      description="Step-by-step tutorials to help you get the most out of Fleetrify."
      containerClass="pb-10"
    >
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm font-medium text-[#143887] hover:text-blue-800 transition-colors mt-6 mb-6"
      >
        <ChevronLeft className="w-4 h-4" />
        Back
      </button>

      <div className="flex flex-col lg:flex-row gap-6">

        {/* LEFT — Video Player */}
        <div className="flex-1 min-w-0">
          {/* Player */}
          <div className="rounded-2xl overflow-hidden shadow-md bg-black aspect-video w-full">
            {activeVideo.source === "cloudinary" ? (
              <video
                key={activeVideo.id}
                className="w-full h-full"
                poster={thumbnailUrl(activeVideo)}
                title={activeVideo.title}
                controls
                // Hide the player's download button and block right-click
                // "Save video as…" — a deterrent, not real protection.
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                autoPlay={userSelected}
                playsInline
              >
                <source src={mp4Url(activeVideo)} type="video/mp4" />
                {/* If the .mp4 fails, the browser falls back to the original upload */}
                {activeVideo.originalExt && (
                  <source src={`${CLOUDINARY_VIDEO_BASE}/${activeVideo.id}.${activeVideo.originalExt}`} />
                )}
              </video>
            ) : (
              <iframe
                key={activeVideo.id}
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${activeVideo.id}?rel=0`}
                title={activeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>

          {/* Video Info */}
          <div className="mt-4 bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1">
                <span
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${categoryColors[activeVideo.category] ?? "bg-gray-100 text-gray-600"
                    }`}
                >
                  {activeVideo.category}
                </span>
                <h2 className="text-lg font-bold text-[#00032A] mt-2">
                  {activeVideo.title}
                </h2>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                  {activeVideo.description}
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-gray-400 text-sm shrink-0">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {activeVideo.duration}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — Playlist */}
        <div className="w-full lg:w-[340px] shrink-0">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-gray-100 shrink-0">
              <h3 className="text-sm font-bold text-[#00032A]">All Tutorials</h3>
              <p className="text-xs text-gray-400 mt-0.5">{videos.length} videos</p>
            </div>

            {/* Scrollable list — fixed height so it always scrolls */}
            <div
              className="divide-y divide-gray-50 overflow-y-auto"
              style={{ maxHeight: "calc(100vh - 280px)" }}
            >
              {videos.map((video, index) => {
                const isActive = video.id === activeVideo.id;
                return (
                  <button
                    key={video.id}
                    onClick={() => selectVideo(video)}
                    className={`w-full text-left px-4 py-4 flex gap-3 items-start transition-colors ${isActive
                        ? "bg-[#EEF2FB] border-l-4 border-[#143887]"
                        : "hover:bg-gray-50 border-l-4 border-transparent"
                      }`}
                  >
                    {/* Thumbnail */}
                    <div className="relative shrink-0 w-[88px] h-[52px] rounded-lg overflow-hidden bg-gray-200">
                      <img
                        src={thumbnailUrl(video)}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                      {isActive && (
                        <div className="absolute inset-0 bg-[#143887]/60 flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      )}
                      {!isActive && (
                        <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] px-1 rounded">
                          {video.duration}
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[10px] font-semibold text-gray-400">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${categoryColors[video.category] ?? "bg-gray-100 text-gray-500"
                            }`}
                        >
                          {video.category}
                        </span>
                      </div>
                      <p
                        className={`text-xs font-semibold leading-snug line-clamp-2 ${isActive ? "text-[#143887]" : "text-[#00032A]"
                          }`}
                      >
                        {video.title}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
};

export default VideoGuide;